import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchTppCards,
  fetchTppDashboardStats,
  type TppCardSummary,
  type TppCardsPage,
} from '../../../services/transitPassPool.service';
import { useTppDebouncedTrim } from './useTppDebouncedTrim';
import { pickTppFocusAvailableCard, pickTppFocusNeedsChargeCard } from './tppCardPredicates';
import { scrollTppCardHistoryIntoView } from './tppUiShared';

export const TPP_CARD_PAGE_SIZE = 50;

type Stats = {
  totalCards: number;
  checkedOutCards: number;
  availableCards: number;
  needsChargeCards: number;
};

type Args = {
  zipConnected: boolean;
  poolKey: string;
  /** Zip 切替時に自動選択を再開するためのセッション鍵（ファイル名など） */
  zipSessionKey?: string | null;
  /** Zip 全体の枚数。未取得時は undefined（空登録ヒントを出さない） */
  accessTotalPoolCards: number | undefined;
  selected: TppCardSummary | null;
  setSelected: (card: TppCardSummary | null) => void;
};

/**
 * カード一覧のフィルタ・検索・ページ・統計・選択同期。
 * ページ本体から一覧状態を切り離す。
 */
export function useTppCardList({
  zipConnected,
  poolKey,
  zipSessionKey = null,
  accessTotalPoolCards,
  selected,
  setSelected,
}: Args) {
  const [showInactiveCards, setShowInactiveCards] = useState(false);
  const [cardPage, setCardPage] = useState(1);
  const [cardSearchInput, setCardSearchInput] = useState('');
  const [cardSearch, flushCardSearch] = useTppDebouncedTrim(cardSearchInput);
  const [checkedOutOnly, setCheckedOutOnlyState] = useState(false);
  /** 初回読み込み時のみ貸出中などを自動選択。ユーザーが解除したら再選択しない */
  const allowAutoSelectRef = useRef(true);
  /** 概況の「貸出中」トグル ON 直後、結果が揃ったら先頭を選ぶ */
  const selectAfterCheckedOutFilterRef = useRef(false);
  /** 「貸出可能」クリック直後、フィルタ解除後に貸出可カードを選ぶ */
  const selectAfterAvailableFocusRef = useRef(false);
  /** 「要チャージ」クリック直後、フィルタ解除後に要チャージカードを選ぶ */
  const selectAfterNeedsChargeFocusRef = useRef(false);

  useEffect(() => {
    setCardPage(1);
  }, [poolKey, showInactiveCards, checkedOutOnly, cardSearch]);

  /** Zip 切替・再接続時は前セッションの検索／フィルタを残さない（空プールが「フィルタ結果0」に見えるのを防ぐ） */
  useEffect(() => {
    allowAutoSelectRef.current = true;
    setShowInactiveCards(false);
    setCheckedOutOnlyState(false);
    setCardPage(1);
    setCardSearchInput('');
    flushCardSearch('');
    selectAfterCheckedOutFilterRef.current = false;
    selectAfterAvailableFocusRef.current = false;
    selectAfterNeedsChargeFocusRef.current = false;
  }, [poolKey, zipConnected, zipSessionKey, flushCardSearch]);

  const cardsQuery = useQuery({
    queryKey: ['tpp-cards', poolKey, showInactiveCards, cardPage, cardSearch, checkedOutOnly],
    queryFn: () =>
      fetchTppCards(poolKey, {
        includeInactive: showInactiveCards,
        page: cardPage,
        pageSize: TPP_CARD_PAGE_SIZE,
        search: cardSearch || undefined,
        checkedOutOnly,
      }),
    enabled: zipConnected,
  });

  const statsQuery = useQuery({
    queryKey: ['tpp-dashboard-stats', poolKey, showInactiveCards],
    queryFn: () => fetchTppDashboardStats(poolKey, { includeInactive: showInactiveCards }),
    enabled: zipConnected,
  });

  const clearCardSelection = useCallback(() => {
    allowAutoSelectRef.current = false;
    setSelected(null);
  }, [setSelected]);

  /** ユーザー操作での選択（履歴へスクロール）。同期・初回自動選択は setSelected のまま */
  const selectCard = useCallback(
    (card: TppCardSummary) => {
      setSelected(card);
      scrollTppCardHistoryIntoView();
    },
    [setSelected]
  );

  const setCheckedOutOnly = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setCheckedOutOnlyState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (next && !prev) selectAfterCheckedOutFilterRef.current = true;
      return next;
    });
  }, []);

  /** 概況クリック: 貸出中フィルタ中なら解除してから対象カードを選ぶ */
  const focusCardAfterClearingCheckoutFilter = useCallback(
    (
      pendingRef: { current: boolean },
      pick: (items: TppCardSummary[]) => TppCardSummary | undefined
    ) => {
      allowAutoSelectRef.current = true;
      if (checkedOutOnly) {
        pendingRef.current = true;
        setCheckedOutOnlyState(false);
        return;
      }
      const found = pick(cardsQuery.data?.items ?? []);
      if (found) selectCard(found);
    },
    [checkedOutOnly, cardsQuery.data?.items, selectCard]
  );

  const focusAvailableCard = useCallback(() => {
    focusCardAfterClearingCheckoutFilter(
      selectAfterAvailableFocusRef,
      pickTppFocusAvailableCard
    );
  }, [focusCardAfterClearingCheckoutFilter]);

  const focusNeedsChargeCard = useCallback(() => {
    focusCardAfterClearingCheckoutFilter(
      selectAfterNeedsChargeFocusRef,
      pickTppFocusNeedsChargeCard
    );
  }, [focusCardAfterClearingCheckoutFilter]);

  /** Esc で選択解除（モーダル表示中は無視） */
  useEffect(() => {
    if (!zipConnected || !selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.modal.show')) return;
      clearCardSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zipConnected, selected, clearCardSelection]);

  /** 貸出・返却後も selected を一覧の最新行に同期（openEventId 欠けで返却不能になるのを防ぐ） */
  useEffect(() => {
    const items = cardsQuery.data?.items ?? [];
    if (items.length === 0) {
      if (selected) setSelected(null);
      return;
    }
    if (selectAfterCheckedOutFilterRef.current && checkedOutOnly && !cardsQuery.isFetching) {
      selectAfterCheckedOutFilterRef.current = false;
      allowAutoSelectRef.current = true;
      selectCard(items[0]);
      return;
    }
    if (selectAfterAvailableFocusRef.current && !checkedOutOnly && !cardsQuery.isFetching) {
      selectAfterAvailableFocusRef.current = false;
      const pick = pickTppFocusAvailableCard(items);
      if (pick) selectCard(pick);
      return;
    }
    if (selectAfterNeedsChargeFocusRef.current && !checkedOutOnly && !cardsQuery.isFetching) {
      selectAfterNeedsChargeFocusRef.current = false;
      const pick = pickTppFocusNeedsChargeCard(items);
      if (pick) selectCard(pick);
      return;
    }
    if (selected) {
      const fresh = items.find((c) => c.id === selected.id);
      if (fresh) {
        if (
          fresh.isCheckedOut !== selected.isCheckedOut ||
          fresh.openEventId !== selected.openEventId ||
          fresh.balanceNumber !== selected.balanceNumber ||
          fresh.openEventRoute !== selected.openEventRoute
        ) {
          setSelected(fresh);
        }
        return;
      }
      setSelected(null);
      return;
    }
    if (!allowAutoSelectRef.current) return;
    const preferred =
      items.find((c) => c.isCheckedOut) ??
      [...items].sort((a, b) => a.displayNumber - b.displayNumber)[0];
    setSelected(preferred);
  }, [cardsQuery.data?.items, cardsQuery.isFetching, checkedOutOnly, selected, setSelected, selectCard]);

  const availableCards =
    statsQuery.data != null ? Math.max(0, Number(statsQuery.data.availableCards ?? 0)) : null;
  const needsChargeCards =
    statsQuery.data != null ? Math.max(0, Number(statsQuery.data.needsChargeCards ?? 0)) : null;

  const noListFilters =
    !cardSearch &&
    !cardSearchInput.trim() &&
    !checkedOutOnly &&
    !showInactiveCards;
  /** access 未取得でもカード0かつフィルタ無しなら登録案内（厳密な ===0 だと undefined で潰れる） */
  const poolLooksEmpty =
    cardsQuery.data?.total === 0 &&
    (accessTotalPoolCards == null || accessTotalPoolCards === 0);
  const showRegisterEmptyHint = zipConnected && poolLooksEmpty && noListFilters;

  const listedCardTotal = Number(cardsQuery.data?.total ?? 0);
  /** カタログ枚数（貸出中フィルタで listed が減っても検索の要否は変えない） */
  const catalogSize = Math.max(
    Number(accessTotalPoolCards ?? 0),
    Number(statsQuery.data?.totalCards ?? 0),
    listedCardTotal
  );
  /** 少枚数は概況の compactExtras に任せ、検索が必要なときだけフィルタ Card を出す */
  const showCardSearch =
    catalogSize > 5 || !!cardSearch || cardSearchInput.trim() !== '';
  const showCardFilterBar = !showRegisterEmptyHint && showCardSearch;
  /**
   * 無効カードが無いとき「無効も表示」はノイズ。
   * access（全枚）> 有効のみの stats.total なら無効が存在する。
   * チェック中は常にトグルを残す。
   */
  const activeOnlyTotal = showInactiveCards
    ? listedCardTotal
    : Number(statsQuery.data?.totalCards ?? listedCardTotal);
  const showInactiveToggle =
    showInactiveCards ||
    Number(accessTotalPoolCards ?? 0) > activeOnlyTotal;
  const singleCardPool = catalogSize <= 1;

  const otherCheckedOut = (cardsQuery.data?.items ?? []).filter(
    (c) => c.isCheckedOut && c.id !== selected?.id
  );
  const poolCheckedOut = Number(statsQuery.data?.checkedOutCards ?? 0);
  const otherCheckedOutTotal = Math.max(
    0,
    poolCheckedOut - (selected?.isCheckedOut ? 1 : 0)
  );

  return {
    showRegisterEmptyHint,
    showCardFilterBar,
    showCardSearch,
    showInactiveToggle,
    singleCardPool,
    selected,
    otherCheckedOut,
    otherCheckedOutTotal,
    availableCards,
    needsChargeCards,
    stats: statsQuery.data as Stats | undefined,
    cardsQuery: cardsQuery as UseQueryResult<TppCardsPage, Error>,
    cardPage,
    setCardPage,
    cardSearch,
    cardSearchInput,
    setCardSearchInput,
    flushCardSearch,
    showInactiveCards,
    setShowInactiveCards,
    checkedOutOnly,
    setCheckedOutOnly,
    clearCardSelection,
    selectCard,
    focusAvailableCard,
    focusNeedsChargeCard,
  };
}
